import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { formatDate } from '@frontend/date';
import { useCalendarEventsConfigurationShallowStore } from '@frontend/schedule-helpers';
import { usePreference, usePreferenceStore } from '@frontend/user-preferences';
import { trackOnboardingFlow } from '../../utils/track-onboarding-flow';
import { CalendarEventsEnums } from '../../views/Calendar/types';

dayjs.extend(isSameOrBefore);

export const useOnboardingFlowInfo = () => {
  const isDataInitialized = usePreferenceStore((state) => state.isDataInitialized);
  const isDataError = usePreferenceStore((state) => state.isDataError);
  const [state, setState] = usePreference('ScheduleOnboardingFlowInfo');
  const { setConfiguredCalendarEvent } = useCalendarEventsConfigurationShallowStore('setConfiguredCalendarEvent');
  const { locationTimezoneMap } = SchedulerV3.Helpers.useScheduleLocationTimezoneShallowStore('locationTimezoneMap');

  const isDataFetched = isDataInitialized && !isDataError;
  const shouldShowOnNextLogin =
    !!state.showOnboardingFlowAfter && dayjs().isSameOrBefore(state.showOnboardingFlowAfter);

  const setOnboardingFlowInfo = (info: Partial<SchedulerV3.Types.ScheduleOnboardingFlowInfo>) => {
    if (!isDataFetched) {
      console.warn('Schedule onboarding flow info cannot be set before data is fetched');
      return;
    }

    handleOnboardingFlowEventTracking(state, info);

    setState({ ...state, ...info });
  };

  const setShowOnNextLogin = () => {
    setOnboardingFlowInfo({ showOnboardingFlowAfter: dayjs().add(24, 'hour').toISOString() });
  };

  const resetOnboardingFlowInfo = (isDebug = false) => {
    if (
      isDebug ||
      (['not_started', 'in_progress'].includes(state.status) &&
        !shouldShowOnNextLogin &&
        !!state.showOnboardingFlowAfter)
    ) {
      setState({
        status: 'not_started',
        showOnboardingFlowAfter: '',
        shouldSetSkipWizardStatus: isDebug ? false : state.shouldSetSkipWizardStatus,
      });
    }
  };

  const showAppointmentFormWithInfoPrefilled = ({
    appointmentType,
    contactName,
    appointmentTypeDuration,
  }: {
    appointmentType: string;
    contactName: string;
    appointmentTypeDuration: number;
  }) => {
    const locationId = state.locationId ?? '';

    const locationTimezone = locationTimezoneMap[locationId];
    const startDate = locationTimezone.timezone
      ? dayjs().tz(locationTimezone.timezone).startOf('hour').add(1, 'hour')
      : dayjs().startOf('hour').add(1, 'hour');

    setConfiguredCalendarEvent({
      calendarDateValue: formatDate(startDate, 'MM/DD/YYYY'),
      startDate: formatDate(startDate, 'MM/DD/YYYY'),
      startHour: formatDate(startDate, 'hh:mm A'),
      endDate: formatDate(startDate.add(appointmentTypeDuration, 'm'), 'MM/DD/YYYY'),
      endHour: formatDate(startDate.add(appointmentTypeDuration, 'm'), 'hh:mm A'),
      calendarEventType: CalendarEventsEnums.APPOINTMENT_EVENT,
      isValid: true,
      name: contactName,
      patientId: state.contactId ?? '',
      providerId: state.providerId ?? '',
      appointmentType,
      locationId,
    });
  };

  const isBookAppointmentVisible =
    isDataFetched &&
    state.status === 'in_progress' &&
    !!state.providerId &&
    !!state.locationId &&
    !!state.contactId &&
    !!state.appointmentTypeId &&
    !shouldShowOnNextLogin;

  const shouldShowOnboardingFlowModal =
    isDataFetched &&
    (!state.status || state.status === 'not_started' || state.status === 'in_progress') &&
    !shouldShowOnNextLogin;
  const shouldShowOnboardingFlowInlineComponent =
    isDataFetched && (state.status === 'not_started' || state.status === 'in_progress');

  const shouldShowAutoMessagesBanner =
    isDataFetched && state.status === 'completed_appointment_creation' && !shouldShowOnNextLogin;

  return {
    isDataFetched,
    onboardingFlowInfo: state,
    resetOnboardingFlowInfo,
    setOnboardingFlowInfo,
    showAppointmentFormWithInfoPrefilled,
    isBookAppointmentVisible,
    shouldShowOnboardingFlowModal,
    shouldShowAutoMessagesBanner,
    shouldShowOnboardingFlowInlineComponent,
    setShowOnNextLogin,
  };
};

const handleOnboardingFlowEventTracking = (
  oldState: SchedulerV3.Types.ScheduleOnboardingFlowInfo,
  newState: Partial<SchedulerV3.Types.ScheduleOnboardingFlowInfo>
) => {
  if (oldState.status === 'not_started' && newState.status === 'in_progress') {
    trackOnboardingFlow('wizardStarted');
  } else if (oldState.status === 'in_progress' && newState.status === 'skipped') {
    trackOnboardingFlow('wizardSkipped');
  } else if (newState.status === 'not_required') {
    trackOnboardingFlow('onboardingFlowNotRequired');
  } else if (!oldState.showOnboardingFlowAfter && newState.showOnboardingFlowAfter) {
    trackOnboardingFlow('skippedToNextLogin');
  }
};
